<script setup>
import { ref, onMounted, computed } from 'vue';
import api from '@/api';
import Swal from 'sweetalert2';
import DuracionFormModal from './components/DuracionFormModal.vue';
import { hasPermission } from '@/composables/useAuth';

const canGestionar = computed(() => hasPermission('membresias_catalogo.gestionar'));

const duraciones = ref([]);
const seleccionada = ref(null);
const modalVisible = ref(false);
const isEditando = ref(false);

const cargar = async () => {
  try {
    const res = await api.get('/duracion', { params: { estado: 'all' } });
    duraciones.value = Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error(err);
    Swal.fire('Error', 'No se pudo cargar duraciones', 'error');
  }
};

const nuevo = () => {
  seleccionada.value = null;
  isEditando.value = false;
  modalVisible.value = true;
};

const editar = (d) => {
  seleccionada.value = { ...d };
  isEditando.value = true;
  modalVisible.value = true;
};

const guardar = async (data) => {
  try {
    if (isEditando.value && data.id) {
      await api.put(`/duracion/${data.id}`, data);
    } else {
      await api.post('/duracion', data);
    }
    modalVisible.value = false;
    await cargar();
    return true;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error('Error en guardarDuracion:', err);
    return err?.response?.data?.message || err.message || 'Error inesperado al guardar';
  }
};

const eliminar = async (d) => {
  Swal.fire({
    title: '¿Desactivar duración?',
    text: `¿Deseas desactivar "${d.nombre}"? Se ocultará de nuevos planes pero conserva el historial.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Sí, desactivar',
    cancelButtonText: 'Cancelar'
  }).then(async (r) => {
    if (r.isConfirmed) {
      try {
        await api.delete(`/duracion/${d.id}`);
        Swal.fire('Desactivada', 'Duración desactivada correctamente', 'success');
        cargar();
      } catch (error) {
        const msg = error.response?.data?.message || 'No se pudo desactivar';
        Swal.fire('Error', msg, 'error');
      }
    }
  });
};

const reactivar = async (d) => {
  try {
    await api.patch(`/duracion/${d.id}/reactivar`);
    Swal.fire('Reactivada', 'Duración reactivada correctamente', 'success');
    cargar();
  } catch (error) {
    const msg = error.response?.data?.message || 'No se pudo reactivar';
    Swal.fire('Error', msg, 'error');
  }
};

onMounted(cargar);
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card p-4">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 class="mb-0">Duraciones</h5>
        <button v-if="canGestionar" class="btn btn-primary btn-sm" @click="nuevo">+ Nueva duración</button>
      </div>
      <small class="text-muted d-block mb-3">fijo=días | variable=pide cantidad (TEMPORADA) | visitas=sin fecha | ilimitado=nunca vence</small>

      <div class="table-responsive">
        <table class="table table-sm align-items-center custom-table text-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>NOMBRE</th>
              <th>MODO</th>
              <th>DÍAS</th>
              <th>ESTADO</th>
              <th class="text-end">ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in duraciones" :key="d.id">
              <td>{{ i + 1 }}</td>
              <td>{{ d.nombre }}</td>
              <td><span class="badge bg-info">{{ d.modo || 'fijo' }}</span></td>
              <td>{{ d.dias_equivalentes }}</td>
              <td><span class="badge" :class="d.estado ? 'bg-success' : 'bg-secondary'">{{ d.estado ? 'Activa' : 'Inactiva' }}</span></td>
              <td class="text-end text-nowrap">
                <button v-if="canGestionar" class="btn btn-outline-secondary btn-xs me-1" title="Editar" @click="editar(d)">
                  <i class="ni ni-settings text-secondary text-xs"></i>
                </button>
                <button v-if="canGestionar && d.estado !== false" class="btn btn-outline-danger btn-xs me-1" title="Desactivar" @click="eliminar(d)">
                  <i class="ni ni-fat-remove text-danger text-xs"></i>
                </button>
                <button v-if="canGestionar && d.estado === false" class="btn btn-outline-success btn-xs me-1" title="Reactivar" @click="reactivar(d)">
                  <i class="ni ni-check-bold text-success text-xs"></i>
                </button>
              </td>
            </tr>
            <tr v-if="duraciones.length === 0">
              <td colspan="6" class="text-center">No hay duraciones. Crea la primera (ej: MES fijo 30).</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <DuracionFormModal :model-value="seleccionada" :visible="modalVisible" :is-edit="isEditando"
      @update:visible="modalVisible = $event" :onSave="guardar" />
  </div>
</template>

<style scoped>
.custom-table th, .custom-table td { padding: 0.25rem 0.4rem !important; font-size: 0.78rem !important; line-height: 1.2; }
.custom-table thead { background-color: #f2f2f2; color: #333; font-size: 0.8rem; text-transform: uppercase; }
.custom-table tbody tr:hover { background-color: #f8f9fa; }
</style>
